import { isRecord } from "../app/typeGuards";
import { isValidBinding } from "./keybinds";
import {
  ACTIONS,
  MAX_BINDINGS_PER_ACTION,
  SECTION_SPECS,
  SETTINGS_KEY,
  SETTINGS_VERSION,
  defaultSettings,
} from "./schema";
import type { Action, Binding, FieldSpec, Settings, SettingsSection } from "./schema";

/** Patch type accepted by `updateSettings`: sections and fields optional, keybinds per action. */
export type SettingsPatch = {
  [S in SettingsSection]?: Partial<Settings[S]>;
} & { keybinds?: Partial<Record<Action, Binding[]>> };

type RawObject = Record<string, unknown>;

// ---- Migration ----

/** Upgrades the blob stored with version N to the shape of version N + 1. */
export type Migration = (raw: RawObject) => RawObject;

/**
 * Registry keyed by the version being upgraded *from*. Versions without an entry are treated as
 * shape compatible and are only re-stamped. Version 0 is a blob without a `version` field
 * (written before versioning existed).
 */
export const MIGRATIONS: Readonly<Record<number, Migration>> = {
  0: (raw) => ({ ...raw, version: 1 }),
};

/**
 * Bring a stored blob to the current version. Returns null when `raw` is not an object.
 * Blobs from a newer client (downgrade) are kept as is: the sanitizing merge drops fields this
 * version does not know.
 */
export function migrateSettings(
  raw: unknown,
  migrations: Readonly<Record<number, Migration>> = MIGRATIONS,
  target: number = SETTINGS_VERSION,
): RawObject | null {
  if (!isRecord(raw)) return null;
  let version = typeof raw.version === "number" && Number.isInteger(raw.version) && raw.version >= 0 ? raw.version : 0;
  let current: RawObject = raw;
  while (version < target) {
    const step = migrations[version];
    current = step ? step(current) : { ...current };
    version++;
    current.version = version;
  }
  return current;
}

// ---- Sanitizing merge ----

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

/** Returns the sanitized patch value, or `fallback` when it is missing or has the wrong type. */
function mergeField(spec: FieldSpec, fallback: unknown, value: unknown): unknown {
  switch (spec.kind) {
    case "number":
      return typeof value === "number" && Number.isFinite(value)
        ? Math.min(spec.max, Math.max(spec.min, value))
        : fallback;
    case "boolean":
      return typeof value === "boolean" ? value : fallback;
    case "enum":
      return typeof value === "string" && spec.values.includes(value) ? value : fallback;
    case "color":
      return typeof value === "string" && HEX_COLOR.test(value) ? value.toLowerCase() : fallback;
  }
}

function mergeSection<S extends SettingsSection>(base: Settings[S], patch: unknown, section: S): Settings[S] {
  const specs = SECTION_SPECS[section] as Record<string, FieldSpec>;
  const patchObj = isRecord(patch) ? patch : {};
  const out: Record<string, unknown> = { ...base };
  for (const key of Object.keys(specs)) {
    const spec = specs[key];
    if (spec) out[key] = mergeField(spec, (base as Record<string, unknown>)[key], patchObj[key]);
  }
  return out as Settings[S];
}

/** Valid, unique bindings of a stored array, capped at the per-action maximum. */
function sanitizeBindings(value: unknown): Binding[] | null {
  if (!Array.isArray(value)) return null;
  const out: Binding[] = [];
  for (const b of value) {
    if (isValidBinding(b) && !out.includes(b)) out.push(b);
    if (out.length >= MAX_BINDINGS_PER_ACTION) break;
  }
  return out;
}

function mergeKeybinds(base: Settings["keybinds"], patch: unknown): Settings["keybinds"] {
  const patchObj = isRecord(patch) ? patch : {};
  const out = {} as Settings["keybinds"];
  for (const action of ACTIONS) {
    out[action] = sanitizeBindings(patchObj[action]) ?? [...base[action]];
  }
  return out;
}

/**
 * Deep merge of `patch` into `base`. Every field is validated against the schema: unknown keys
 * are dropped, wrong types are ignored (the base value stays), numbers are clamped to their
 * range, enums and colors must be valid. Returns a new object, `base` is not modified.
 */
export function mergeSettings(base: Settings, patch: unknown): Settings {
  const p = isRecord(patch) ? patch : {};
  return {
    version: SETTINGS_VERSION,
    controls: mergeSection(base.controls, p.controls, "controls"),
    keybinds: mergeKeybinds(base.keybinds, p.keybinds),
    video: mergeSection(base.video, p.video, "video"),
    audio: mergeSection(base.audio, p.audio, "audio"),
    crosshair: mergeSection(base.crosshair, p.crosshair, "crosshair"),
    hud: mergeSection(base.hud, p.hud, "hud"),
    network: mergeSection(base.network, p.network, "network"),
  };
}

// ---- Storage ----

/** The subset of `Storage` used here (lets tests pass a fake). */
export interface SettingsStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** Parse a stored JSON string: invalid JSON or a non-object gives the defaults. */
export function parseSettings(json: string | null): Settings {
  const defaults = defaultSettings();
  if (!json) return defaults;
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    return defaults;
  }
  const migrated = migrateSettings(raw);
  return migrated ? mergeSettings(defaults, migrated) : defaults;
}

export function loadSettings(storage: SettingsStorage | null): Settings {
  if (!storage) return defaultSettings();
  try {
    return parseSettings(storage.getItem(SETTINGS_KEY));
  } catch {
    return defaultSettings();
  }
}

/** Returns false when the write failed (quota, blocked storage). */
export function saveSettings(storage: SettingsStorage | null, settings: Settings): boolean {
  if (!storage) return false;
  try {
    storage.setItem(SETTINGS_KEY, JSON.stringify(settings));
    return true;
  } catch {
    return false;
  }
}
